import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Container, Row, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { BiRefresh } from "react-icons/bi";
import { FaArrowRight, FaBrain, FaGlobeAsia, FaPhoneAlt, FaRegBell, FaUserClock } from "react-icons/fa";
import { MdAutoAwesome, MdContentCopy, MdInbox, MdOutlineNewReleases, MdWarningAmber } from "react-icons/md";
import axios from "axios";

import NavMenu from "../components/NavMenu";
import Avatar from "../components/Avatar";
import { accessibleBg } from "../utils/color";
import { useTheme } from "../components/ThemeContext";
import "./insights.css";
import "./insightsPage.css";

const API = process.env.REACT_APP_API || "";

const normDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const getLoginName = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.user_name || user?.name || user?.username || user?.email || "User";
  } catch (_) {
    return "User";
  }
};

const shortText = (value, max = 110) => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (text.length <= max) return text || "-";
  return `${text.slice(0, max)}...`;
};

const normalizeHex = (c) => {
  const s = String(c || "").trim();
  if (!s) return "";
  return s.startsWith("#") ? s : `#${s}`;
};

// Black or white text, whichever has the higher WCAG contrast against `bg`
// (same rule the Enquiries/Dashboard chips use).
const getTextColor = (bg) => {
  const hex = normalizeHex(bg).replace("#", "");
  if (hex.length !== 6) return "#ffffff";
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return "#ffffff";
  const toLinear = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const lum = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  return (lum + 0.05) / 0.05 >= 1.05 / (lum + 0.05) ? "#111827" : "#ffffff";
};

// Same three lifecycle states + colors as the Enquiries/Dashboard status badge.
const getStatusInfo = (status) => {
  const s = String(status || "").trim().toLowerCase();
  if (s === "email sent") return { label: "Email Sent", color: "#22c55e" };
  if (s === "responded") return { label: "Responded", color: "#0ea5e9" };
  return { label: "Pending", color: "#FC6C62" };
};

const riskTone = (risk) => {
  const v = String(risk || "").toLowerCase();
  if (v.includes("high")) return "ins-red";
  if (v.includes("medium")) return "ins-amber";
  return "accent-slate";
};

const scoreTone = (score) => (score >= 70 ? "ins-red" : score >= 45 ? "ins-amber" : "accent-slate");

const SectionTitle = ({ icon, accent, children, right }) => (
  <div className="ins-section-head">
    <div className={`ins-section-title ${accent}`}>
      <span className="ins-title-icon">{icon}</span>
      <span>{children}</span>
    </div>
    {right}
  </div>
);

const StatCard = ({ icon, title, value, note, accent, loading, progress, delay }) => (
  <Card className={`ins-card ins-stat ins-fade ${accent}`} style={{ "--d": `${delay}ms` }}>
    <Card.Body>
      <div className="ins-stat-watermark" aria-hidden="true">{icon}</div>
      <div className="ins-stat-top">
        <span className="ins-stat-icon">{icon}</span>
        <span className="ins-stat-title">{title}</span>
      </div>
      {loading ? <span className="ins-skel ins-skel-value" /> : <div className="ins-stat-value" title={String(value ?? 0)}>{value ?? 0}</div>}
      <div className="ins-stat-note">{note}</div>
      {progress != null && !loading && (
        <div className="ins-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
          <span style={{ width: `${progress}%` }} />
        </div>
      )}
    </Card.Body>
  </Card>
);

const InsightTile = ({ icon, accent, label, text }) => (
  <div className={`ins-tile ${accent}`}>
    <div className="ins-tile-head">
      <span className="ins-tile-icon">{icon}</span>
      {label}
    </div>
    <div className="ins-tile-text">{text}</div>
  </div>
);

const EmptyState = ({ text }) => (
  <div className="ins-empty">
    <span className="ins-empty-icon"><MdInbox /></span>
    <div>{text}</div>
  </div>
);

const Chip = ({ label, color }) => (
  <span className="ins-chip" style={{ background: color, color: getTextColor(color) }}>{label}</span>
);

const Insights = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [updatedAt, setUpdatedAt] = useState("");
  const [sources, setSources] = useState([]);

  const loginName = useMemo(() => getLoginName(), []);

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/getEnquiryInsights`);
      if (res.data?.success) {
        setData(res.data.data || {});
        setUpdatedAt(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
      } else {
        setError(res.data?.message || "Failed to load insights.");
      }
    } catch (err) {
      console.error("getEnquiryInsights error:", err);
      setError(err?.response?.data?.message || "Server error. Failed to load insights.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  // Source colors are the ones set in Data Center, so badges match the
  // Enquiries/Dashboard pages. If this fails the badges just use a neutral grey.
  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/getSources`)
      .then((res) => {
        if (alive && res.data?.success) setSources(res.data.data || []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const sourceColorMap = useMemo(
    () =>
      new Map(
        sources.map((s) => [String(s.source || "").trim().toLowerCase(), normalizeHex(s.color)]),
      ),
    [sources],
  );
  const sourceColor = (name) =>
    accessibleBg(sourceColorMap.get(String(name || "").trim().toLowerCase()) || "#64748b");

  const stats = data?.stats || {};
  const ai = data?.ai || {};
  const countryCounts = (data?.countryCounts || []).slice(0, 8);
  const newEnquiries = data?.newEnquiries || [];
  const duplicates = data?.duplicates || [];
  const contactsToday = data?.contactsToday || [];
  const whoToWatch = ai?.who_to_watch || [];
  const aiContactToday = ai?.contact_today || [];

  const waiting = loading && !data;
  const tableClass = `ins-table ${isDark ? "table-dark" : ""}`;

  const unrepliedPct =
    stats.total_active > 0 ? Math.min(100, Math.round(((stats.unreplied || 0) / stats.total_active) * 100)) : null;

  const countryMax = Math.max(1, ...countryCounts.map((c) => c.count));
  const countryBase = stats.total_active || countryCounts.reduce((sum, c) => sum + c.count, 0);

  return (
    <Container fluid className={`min-vh-100 p-3 p-md-4 ins-page ${isDark ? "dark-theme" : "light-theme"}`}>
      <NavMenu />

      <div className="ins-hero ins-fade">
        <div className="ins-hero-text">
          <span className="ins-hero-chip"><MdAutoAwesome /> Live AI overview</span>
          <h1 className="ins-hero-title">Hello, {loginName}</h1>
          <p className="ins-hero-sub">
            AI-powered enquiry overview, duplicate checks, countries and contact priorities.
          </p>
        </div>
        <div className="ins-hero-actions">
          {updatedAt && <span className="ins-hero-updated">Updated {updatedAt}</span>}
          <Button className="ins-hero-btn" onClick={fetchInsights} disabled={loading}>
            {loading ? <Spinner size="sm" className="me-2" /> : <BiRefresh className="me-2" size={18} />}
            Refresh Insights
          </Button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <Row className="g-3 mb-3">
        <Col xs={12} md={6} xl={3}>
          <StatCard
            accent="accent-blue" delay={0} loading={waiting}
            icon={<MdOutlineNewReleases />} title="New Today" value={stats.today_new}
            note="New enquiries received today"
          />
        </Col>
        <Col xs={12} md={6} xl={3}>
          <StatCard
            accent="accent-magenta" delay={70} loading={waiting}
            icon={<FaRegBell />} title="Yet to Reply" value={stats.unreplied}
            note={unrepliedPct != null ? `${unrepliedPct}% of active enquiries need follow-up` : "Need follow-up before they go cold"}
            progress={unrepliedPct}
          />
        </Col>
        <Col xs={12} md={6} xl={3}>
          <StatCard
            accent="accent-violet" delay={140} loading={waiting}
            icon={<MdContentCopy />} title="Duplicate Groups" value={stats.possible_duplicate_groups}
            note="Same usable numeric phone number"
          />
        </Col>
        <Col xs={12} md={6} xl={3}>
          <StatCard
            accent="accent-teal" delay={210} loading={waiting}
            icon={<FaGlobeAsia />} title="Top Country" value={stats.top_country || "-"}
            note={`Top source: ${stats.top_source || "-"}`}
          />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col xs={12} xl={7}>
          <Card className="ins-card ins-fade h-100" style={{ "--d": "120ms" }}>
            <Card.Body>
              <SectionTitle
                accent="accent-violet"
                icon={<FaBrain />}
                right={<span className="ins-ai-chip"><MdAutoAwesome /> AI</span>}
              >
                Intelligent Overview
              </SectionTitle>
              {waiting ? (
                <>
                  <span className="ins-skel ins-skel-line" style={{ width: "100%" }} />
                  <span className="ins-skel ins-skel-line" style={{ width: "94%" }} />
                  <span className="ins-skel ins-skel-line" style={{ width: "76%" }} />
                </>
              ) : (
                <>
                  <p className="ins-summary">{ai.summary || "No AI summary available yet."}</p>
                  <Row className="g-2">
                    <Col md={6}>
                      <InsightTile accent="accent-teal" icon={<FaGlobeAsia />} label="Country Insight" text={ai.country_insight || "-"} />
                    </Col>
                    <Col md={6}>
                      <InsightTile accent="accent-blue" icon={<MdOutlineNewReleases />} label="New Enquiry Insight" text={ai.new_enquiry_insight || "-"} />
                    </Col>
                    <Col md={12}>
                      <InsightTile accent="accent-violet" icon={<MdContentCopy />} label="Duplicate Note" text={ai.duplicate_notes || "-"} />
                    </Col>
                    <Col md={12}>
                      <InsightTile
                        accent="accent-magenta"
                        icon={<FaRegBell />}
                        label="Priority Insight"
                        text={ai.priority_insight || "Priority is based on unreplied enquiries and details that show clear contact intent."}
                      />
                    </Col>
                  </Row>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} xl={5}>
          <Card className="ins-card ins-fade h-100" style={{ "--d": "190ms" }}>
            <Card.Body>
              <SectionTitle
                accent="accent-green"
                icon={<FaUserClock />}
                right={aiContactToday.length > 0 && <span className="ins-count accent-green">{Math.min(aiContactToday.length, 5)}</span>}
              >
                Who Should Contact Today
              </SectionTitle>
              {aiContactToday.length === 0 ? (
                <EmptyState text="No contact suggestion found." />
              ) : (
                aiContactToday.slice(0, 5).map((item, index) => (
                  <div className="ins-contact" key={`ai-contact-${index}-${item.name || "person"}`}>
                    <Avatar name={item.name} size={40} />
                    <div className="ins-contact-body">
                      <div className="ins-contact-head">
                        <strong>{item.name || "Unnamed Contact"}</strong>
                        <span className="ins-pill accent-green">Contact Today</span>
                      </div>
                      <div className="ins-contact-why">{item.why}</div>
                      {item.suggested_message && (
                        <div className="ins-action"><b>Action:</b> {item.suggested_message}</div>
                      )}
                      <button
                        type="button"
                        className="ins-btn"
                        disabled={!item.id}
                        onClick={() => navigate(`/enquire?highlight=${item.id}`)}
                        title={item.id ? undefined : "Could not match this suggestion to an enquiry"}
                      >
                        Take Action <FaArrowRight size={11} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col xs={12} xl={6}>
          <Card className="ins-card ins-fade h-100" style={{ "--d": "240ms" }}>
            <Card.Body>
              <SectionTitle accent="ins-amber" icon={<MdWarningAmber />}>Who to Watch</SectionTitle>
              {whoToWatch.length === 0 ? (
                <EmptyState text="No watch item found." />
              ) : (
                <Table responsive hover className={`${tableClass} ins-watch-table`}>
                  <thead><tr><th>Name</th><th>Risk</th><th>Why</th></tr></thead>
                  <tbody>
                    {whoToWatch.slice(0, 8).map((item, index) => (
                      <tr key={`watch-${index}-${item.name || "person"}`}>
                        <td>
                          <div className="ins-person">
                            <Avatar name={item.name} size={32} />
                            <span className="ins-person-name">{item.name || "Unnamed Contact"}</span>
                          </div>
                        </td>
                        <td><span className={`ins-pill ${riskTone(item.risk)}`}>{item.risk || "Low"}</span></td>
                        <td>{item.why || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} xl={6}>
          <Card className="ins-card ins-fade h-100" style={{ "--d": "300ms" }}>
            <Card.Body>
              <SectionTitle accent="accent-teal" icon={<FaGlobeAsia />}>Most Enquiry Countries</SectionTitle>
              {countryCounts.length === 0 ? (
                <EmptyState text="No country data found." />
              ) : (
                <div className="ins-countries">
                  {countryCounts.map((item, index) => (
                    <div className="ins-country-row" key={item.name}>
                      <span className={`ins-rank ${index < 3 ? `r${index + 1}` : ""}`}>{index + 1}</span>
                      <div className="ins-country-main">
                        <div className="ins-country-line">
                          <span className="ins-country-name" title={item.name}>{item.name}</span>
                          <span className="ins-country-count">
                            {item.count}
                            {countryBase > 0 && <small>{Math.round((item.count / countryBase) * 100)}%</small>}
                          </span>
                        </div>
                        <div className="ins-bar"><span style={{ width: `${(item.count / countryMax) * 100}%` }} /></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        <Col xs={12} xl={7}>
          <Card className="ins-card ins-fade" style={{ "--d": "340ms" }}>
            <Card.Body>
              <SectionTitle
                accent="accent-blue"
                icon={<MdOutlineNewReleases />}
                right={newEnquiries.length > 0 && <span className="ins-count accent-blue">{newEnquiries.length}</span>}
              >
                New / Recent Enquiries
              </SectionTitle>
              {newEnquiries.length === 0 ? (
                <EmptyState text="No new enquiry found." />
              ) : (
                <div className="insight-scroll-box">
                  <Table responsive hover className={tableClass}>
                    <thead><tr><th>Date</th><th>Name</th><th>Country</th><th>Source</th><th>Enquiry</th></tr></thead>
                    <tbody>
                      {newEnquiries.map((item, index) => (
                        <tr key={`new-${index}-${item.name || "person"}`}>
                          <td className="ins-nowrap">{normDate(item.submission_date)}</td>
                          <td>
                            <div className="ins-person">
                              <Avatar name={item.name} size={32} />
                              <div>
                                <div className="ins-person-name">{item.name}</div>
                                <div className="ins-person-sub">{item.email || item.phone_number}</div>
                              </div>
                            </div>
                          </td>
                          <td>{item.country || "-"}</td>
                          <td><Chip label={item.source || "-"} color={sourceColor(item.source)} /></td>
                          <td>{shortText(item.enquiry)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} xl={5}>
          <Card className="ins-card ins-fade" style={{ "--d": "400ms" }}>
            <Card.Body>
              <SectionTitle
                accent="ins-amber"
                icon={<MdContentCopy />}
                right={duplicates.length > 0 && <span className="ins-count ins-amber">{duplicates.length}</span>}
              >
                Duplicate Enquiries
              </SectionTitle>
              {duplicates.length === 0 ? (
                <EmptyState text="No possible duplicate found." />
              ) : (
                <div className="insight-scroll-box">
                  {duplicates.slice(0, 8).map((group, idx) => (
                    <div className="ins-dup" key={`dup-${idx}`}>
                      <div className="ins-dup-head">
                        <strong>{group.count} matching enquiries</strong>
                        <span className="ins-pill ins-amber">{group.match_by}</span>
                      </div>
                      <div className="ins-person-sub">Latest: {normDate(group.latest_date)}</div>
                      <div className="ins-dup-phone"><FaPhoneAlt size={11} /> {group.phone_number || "-"}</div>
                      {(group.enquiries || []).slice(0, 4).map((item, itemIndex) => (
                        <div className="ins-dup-person" key={`dup-person-${idx}-${itemIndex}`}>
                          <Avatar name={item.name} size={24} />
                          <div>
                            <strong>{item.name || "No Name"}</strong> ({item.phone_number || "No numeric phone"}) — {shortText(item.enquiry_summary, 70)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="ins-card ins-fade mt-3" style={{ "--d": "460ms" }}>
        <Card.Body>
          <SectionTitle
            accent="accent-magenta"
            icon={<FaUserClock />}
            right={contactsToday.length > 0 && <span className="ins-count accent-magenta">{Math.min(contactsToday.length, 10)}</span>}
          >
            Priority Results - Need Contact
          </SectionTitle>
          {contactsToday.length === 0 ? (
            <EmptyState text="No priority contacts found." />
          ) : (
            <Table responsive hover className={tableClass}>
              <thead><tr><th>Score</th><th>Name / Enquiry Summary</th><th>Contact</th><th>Country</th><th>Status</th><th>Reason</th></tr></thead>
              <tbody>
                {contactsToday.slice(0, 10).map((item, index) => {
                  const status = getStatusInfo(item.status);
                  return (
                    <tr key={`contact-${index}-${item.name || "person"}`}>
                      <td><span className={`ins-score ${scoreTone(item.priority_score)}`}>{item.priority_score}</span></td>
                      <td>
                        <div className="ins-person">
                          <Avatar name={item.name} size={34} />
                          <div>
                            <div className="ins-person-name">{item.name}</div>
                            <div className="ins-person-sub">{shortText(item.enquiry_summary, 90)}</div>
                          </div>
                        </div>
                      </td>
                      <td>{item.email || item.phone_number || "-"}</td>
                      <td>{item.country || "-"}</td>
                      <td><Chip label={status.label} color={status.color} /></td>
                      <td className="ins-reason">{item.reason}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Insights;
